import React, { useState, useEffect, useCallback } from "react";
import {
  Activity, CheckCircle, AlertTriangle, XCircle, Loader2, RefreshCw,
  Mail, Send, FileText, TrendingUp, Clock, Zap, Eye, Gauge,
  AlertCircle, BarChart3, Star, CreditCard, MessageSquare, TrendingDown
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEV_META = {
  P0: { label: "P0", cls: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
  P1: { label: "P1", cls: "bg-amber-100 text-amber-700 border-amber-200", icon: AlertTriangle },
  P2: { label: "P2", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: Eye },
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

// ─── Sub-components ──────────────────────────────────────────

const HealthTile = ({ icon: Icon, label, value, sub, tone = "emerald" }) => {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber:   "bg-amber-50   text-amber-700   border-amber-100",
    red:     "bg-red-50     text-red-700     border-red-100",
    indigo:  "bg-indigo-50  text-indigo-700  border-indigo-100",
  }[tone];
  return (
    <div className={`rounded-2xl border p-4 ${tones}`} data-testid={`health-tile-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} />
        <span className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</span>
      </div>
      <p className="text-2xl font-black">{value}</p>
      {sub && <p className="text-[10px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
};

const ProbeRow = ({ r }) => {
  const ok = r.ok;
  const tone = !ok ? "text-red-600" : r.latency_ms > 1500 ? "text-amber-600" : "text-emerald-600";
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 text-xs font-mono" data-testid={`probe-row-${r.path}`}>
      <span className={`w-2 h-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
      <span className="text-gray-400 w-12">{r.method}</span>
      <span className="flex-1 truncate">{r.path}</span>
      <span className={`w-12 text-right ${tone}`}>{r.status ?? "—"}</span>
      <span className={`w-16 text-right ${tone}`}>{r.latency_ms}ms</span>
    </div>
  );
};

const IssueCard = ({ issue, onResolve }) => {
  const meta = SEV_META[issue.severity] || SEV_META.P2;
  const Icon = meta.icon;
  return (
    <div className={`rounded-xl border p-3 ${meta.cls}`} data-testid={`dg-issue-${issue.id || issue.signature}`}>
      <div className="flex items-start gap-2">
        <Icon size={14} className="mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-white/60">{meta.label}</span>
            <span className="text-[9px] uppercase text-gray-500">{issue.kind}</span>
            {issue.occurrences > 1 && <span className="text-[9px] text-gray-500">· {issue.occurrences}x</span>}
          </div>
          <p className="text-xs font-bold mt-1 break-words">
            {issue.title || issue.exception || "(no title)"}
          </p>
          {issue.message && (
            <p className="text-[11px] text-gray-600 mt-0.5 break-words line-clamp-2">{issue.message}</p>
          )}
          {issue.sample_traceback && (
            <details className="mt-1.5">
              <summary className="text-[10px] cursor-pointer text-gray-500 hover:text-gray-700">Stack trace</summary>
              <pre className="text-[9px] bg-white/60 rounded p-2 mt-1 overflow-x-auto whitespace-pre-wrap">{issue.sample_traceback}</pre>
            </details>
          )}
          <p className="text-[10px] text-gray-500 mt-1">Last seen: {fmtTime(issue.last_seen)}</p>
        </div>
        <button
          onClick={() => onResolve(issue)}
          className="px-2 py-0.5 rounded-md bg-white/70 hover:bg-white text-[10px] font-bold text-gray-700"
          data-testid={`resolve-${issue.id || issue.signature}`}
        >
          Resolve
        </button>
      </div>
    </div>
  );
};

// ─── Main tab ────────────────────────────────────────────────

const FrictionTile = ({ icon: Icon, label, value, tone = "slate" }) => {
  const tones = {
    red:   "bg-red-50 text-red-700 border-red-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    slate: "bg-slate-50 text-slate-600 border-slate-100",
  }[tone];
  const hot = value > 0;
  return (
    <div className={`rounded-xl border p-2 ${hot ? tones : 'bg-gray-50 text-gray-400 border-gray-100'}`}
         data-testid={`friction-tile-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center gap-1"><Icon size={11} /><span className="text-[9px] uppercase font-bold opacity-70">{label}</span></div>
      <p className="text-lg font-black">{value}</p>
    </div>
  );
};

const DoozyGuardTab = ({ authAxios }) => {
  const [data, setData] = useState(null);
  const [friction, setFriction] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [usage, setUsage] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [synthetic, setSynthetic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, f, c, u, a, syn] = await Promise.all([
        authAxios.get(`${API_URL}/api/doozyguard/status`),
        authAxios.get(`${API_URL}/api/doozyguard/friction`).catch(() => ({ data: null })),
        authAxios.get(`${API_URL}/api/doozyguard/capacity/scorecard`).catch(() => ({ data: null })),
        authAxios.get(`${API_URL}/api/doozyguard/usage`).catch(() => ({ data: null })),
        authAxios.get(`${API_URL}/api/doozyguard/anomalies`).catch(() => ({ data: { anomalies: [] } })),
        authAxios.get(`${API_URL}/api/doozyguard/synthetic/recent?limit=12`).catch(() => ({ data: null })),
      ]);
      setData(s.data);
      setFriction(f.data);
      setCapacity(c.data);
      setUsage(u.data);
      setAnomalies(a.data?.anomalies || []);
      setSynthetic(syn.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => {
    load();
    const int = setInterval(load, 30 * 1000); // refresh every 30s
    return () => clearInterval(int);
  }, [load]);

  const runAction = async (kind) => {
    setBusy(kind);
    try {
      await authAxios.post(`${API_URL}/api/doozyguard/run/${kind}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Action failed");
    }
    setBusy("");
  };

  const resolveIssue = async (issue) => {
    const note = window.prompt(`Resolve "${issue.title || issue.exception}" — add a note (optional):`, "");
    if (note === null) return;
    try {
      await authAxios.post(`${API_URL}/api/doozyguard/issues/${issue.id}/resolve`, { note });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-[#E83E8C]" /></div>;
  }
  if (!data) {
    return <p className="text-center text-sm text-gray-500 py-20">Could not load DoozyGuard status.</p>;
  }

  const probe = data.last_probe || {};
  const probeOk = probe.ok || 0;
  const probeTotal = probe.total || 0;
  const okPct = probeTotal ? Math.round((probeOk / probeTotal) * 100) : 0;
  const sevCounts = data.severity_counts || {};
  const globalTone = sevCounts.P0 > 0 ? "red" : sevCounts.P1 > 0 ? "amber" : "emerald";

  return (
    <div className="space-y-5" data-testid="doozyguard-tab">
      {/* Header + global status */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-[#1E1B4B] flex items-center gap-2">
            <Activity size={20} className="text-[#E83E8C]" /> DoozyGuard
          </h2>
          <p className="text-xs text-gray-500">Autonomous QA + SRE for DoozyGo · v1 (eyes open)</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => runAction("probe")}
            disabled={busy === "probe"}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1E1B4B] text-white text-xs font-bold hover:bg-[#312E81] disabled:opacity-50"
            data-testid="run-probe-btn"
          >
            {busy === "probe" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Run probe
          </button>
          <button
            onClick={() => runAction("digest")}
            disabled={busy === "digest"}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 disabled:opacity-50"
            data-testid="run-digest-btn"
          >
            {busy === "digest" ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} Scan logs
          </button>
          <button
            onClick={() => runAction("anomalies")}
            disabled={busy === "anomalies"}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-500 text-white text-xs font-bold hover:bg-rose-600 disabled:opacity-50"
            data-testid="run-anomalies-btn"
          >
            {busy === "anomalies" ? <Loader2 size={12} className="animate-spin" /> : <TrendingDown size={12} />} Anomaly scan
          </button>
          <button
            onClick={() => runAction("synthetic")}
            disabled={busy === "synthetic"}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 disabled:opacity-50"
            data-testid="run-synthetic-btn"
          >
            {busy === "synthetic" ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />} Run synthetic
          </button>
          <button
            onClick={() => runAction("brief")}
            disabled={busy === "brief"}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E83E8C] text-white text-xs font-bold hover:bg-[#c22d78] disabled:opacity-50"
            data-testid="send-brief-btn"
          >
            {busy === "brief" ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send brief now
          </button>
        </div>
      </div>

      {/* Health grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <HealthTile
          icon={CheckCircle}
          label="Endpoints OK"
          value={`${probeOk}/${probeTotal}`}
          sub={`${okPct}% healthy`}
          tone={globalTone}
        />
        <HealthTile icon={Zap} label="Avg Latency" value={`${probe.avg_latency_ms || 0}ms`}
                    sub={`last probe: ${fmtTime(probe.ran_at)}`} tone={(probe.avg_latency_ms || 0) > 1500 ? "amber" : "emerald"} />
        <HealthTile icon={AlertTriangle} label="Open Issues"
                    value={(sevCounts.P0 || 0) + (sevCounts.P1 || 0) + (sevCounts.P2 || 0)}
                    sub={`${sevCounts.P0 || 0} P0 · ${sevCounts.P1 || 0} P1 · ${sevCounts.P2 || 0} P2`}
                    tone={globalTone} />
        <HealthTile icon={TrendingUp} label="24h Bookings"
                    value={data.metrics.bookings_24h}
                    sub={`₹${(data.metrics.revenue_24h || 0).toLocaleString('en-IN')} revenue`}
                    tone="indigo" />
      </div>

      {/* Last probe details + open issues */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm" data-testid="probe-details">
          <h3 className="font-bold text-sm text-[#1E1B4B] mb-2 flex items-center gap-1.5">
            <Activity size={14} className="text-[#E83E8C]" /> Last Probe
          </h3>
          {probe.results ? (
            <div className="space-y-0.5 max-h-[400px] overflow-y-auto">
              {probe.results.map((r) => <ProbeRow key={r.path} r={r} />)}
            </div>
          ) : (
            <p className="text-xs text-gray-500">No probe runs yet.</p>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm" data-testid="issues-list">
          <h3 className="font-bold text-sm text-[#1E1B4B] mb-2 flex items-center gap-1.5">
            <AlertTriangle size={14} className="text-amber-500" /> Open Issues ({data.open_issues.length})
          </h3>
          {data.open_issues.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle size={28} className="mx-auto text-emerald-500 mb-2" />
              <p className="text-xs text-gray-500">All clear — no open issues 🎉</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {data.open_issues.map((i) => (
                <IssueCard key={i.id || i.signature} issue={i} onResolve={resolveIssue} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* v2: Business Anomalies */}
      {anomalies.length > 0 && (
        <div className="bg-white rounded-2xl border border-rose-200 p-4 shadow-sm" data-testid="anomalies-card">
          <h3 className="font-bold text-sm text-[#1E1B4B] mb-2 flex items-center gap-1.5">
            <TrendingDown size={14} className="text-rose-500" /> Business Anomalies ({anomalies.length})
          </h3>
          <div className="space-y-2">
            {anomalies.map((a) => (
              <div key={a.id} className="rounded-xl border border-rose-100 bg-rose-50 p-3" data-testid={`anomaly-${a.anomaly_key}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${a.severity === 'P0' ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}>{a.severity}</span>
                  <span className="text-[9px] uppercase text-rose-700 font-bold">{(a.anomaly_key || '').replace(/_/g, ' ')}</span>
                  <span className="ml-auto text-[10px] text-gray-500">last: {fmtTime(a.last_seen)}</span>
                </div>
                <p className="font-bold text-sm text-[#1E1B4B] mt-1">{a.title}</p>
                <p className="text-[11px] text-gray-700 mt-0.5">{a.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* v2: Customer Friction */}
      {friction && (
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm" data-testid="friction-card">
          <h3 className="font-bold text-sm text-[#1E1B4B] mb-3 flex items-center gap-1.5">
            <AlertCircle size={14} className="text-amber-500" /> Customer Friction · last 24h
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <FrictionTile icon={Star}         label="Low ratings"       value={friction.totals.low_ratings} tone="amber" />
            <FrictionTile icon={CreditCard}   label="Stuck payments"    value={friction.totals.stuck_payments} tone="red" />
            <FrictionTile icon={MessageSquare} label="Bot escalations"  value={friction.totals.bot_escalations} tone="amber" />
            <FrictionTile icon={TrendingDown} label="Refund requests"   value={friction.totals.refund_requests} tone="red" />
            <FrictionTile icon={XCircle}      label="Cancellations"     value={friction.totals.cancellations_24h} tone="slate" />
            <FrictionTile icon={AlertCircle}  label="Pay webhook errs"  value={friction.totals.payment_webhook_errors_open} tone="red" />
          </div>
          {friction.totals.low_ratings > 0 && friction.low_ratings.length > 0 && (
            <div className="mt-3 space-y-1">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Recent low ratings</p>
              {friction.low_ratings.slice(0, 3).map((r) => (
                <div key={r.id} className="text-xs bg-amber-50 rounded-lg px-2 py-1.5 text-amber-800" data-testid={`low-rating-${r.id}`}>
                  <b>{r.customer_name}</b> · {r.rating}★ · {r.package} — {r.review?.slice(0, 80) || '(no review)'}
                </div>
              ))}
            </div>
          )}
          {friction.totals.stuck_payments > 0 && friction.stuck_payments.length > 0 && (
            <div className="mt-3 space-y-1">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Stuck payments &gt; 30 min</p>
              {friction.stuck_payments.slice(0, 3).map((p) => (
                <div key={p.id} className="text-xs bg-red-50 rounded-lg px-2 py-1.5 text-red-800" data-testid={`stuck-payment-${p.id}`}>
                  <b>{p.customer_name}</b> · {p.customer_phone} · ₹{p.total_amount?.toLocaleString('en-IN')} · {p.package} · status: {p.payment_status || 'unknown'}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* v3: Capacity Scorecard */}
      {capacity && (
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm" data-testid="capacity-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm text-[#1E1B4B] flex items-center gap-1.5">
              <Gauge size={14} className="text-indigo-500" /> Capacity Readiness
            </h3>
            <span className="text-[10px] text-gray-500">
              Current load: <b className="text-[#1E1B4B]">{capacity.daily_events_7d_avg} events/day</b>
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2" data-testid="capacity-tiers">
            {capacity.tiers.map((t) => (
              <div
                key={t.id}
                className={`rounded-xl border p-3 ${
                  t.is_current ? 'border-emerald-400 bg-emerald-50'
                    : t.is_next ? 'border-amber-400 bg-amber-50'
                    : 'border-gray-200 bg-gray-50 opacity-70'
                }`}
                data-testid={`capacity-tier-${t.id}`}
              >
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
                  {t.is_current ? '📍 You are here' : t.is_next ? '⏭ Next milestone' : ''}
                </p>
                <p className="font-black text-lg text-[#1E1B4B]">{t.events_per_day}/day</p>
                <p className="text-[10px] text-gray-500">~₹{((t.monthly_revenue_inr || 0) / 10000000).toFixed(1)} Cr/mo</p>
                <div className="mt-2 space-y-0.5">
                  {(t.checks || []).slice(0, 5).map((c, i) => (
                    <div key={`${c.label}-${i}`} className="flex items-start gap-1 text-[10px]">
                      <span className="shrink-0">
                        {c.kind === 'ok' ? '✅' : c.kind === 'warn' ? '🟡' : '🔴'}
                      </span>
                      <span className="text-gray-700 line-clamp-1" title={c.why || ''}>{c.label}</span>
                    </div>
                  ))}
                  {(t.checks || []).length > 5 && (
                    <p className="text-[9px] text-gray-400 italic">+{t.checks.length - 5} more</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {capacity.next_tier_action_count > 0 && (
            <p className="text-xs text-amber-700 mt-3 font-bold">
              🎯 {capacity.next_tier_action_count} infra changes needed to comfortably hit the next tier.
            </p>
          )}
        </div>
      )}

      {/* v2: Feature Usage */}
      {usage && usage.total_routes_active > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm" data-testid="usage-card">
          <h3 className="font-bold text-sm text-[#1E1B4B] mb-2 flex items-center gap-1.5">
            <BarChart3 size={14} className="text-indigo-500" /> Feature Usage · last {usage.window_days}d
          </h3>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Top routes</p>
              <div className="space-y-1">
                {usage.top_used.slice(0, 10).map((t, i) => (
                  <div key={`${t.method}-${t.pattern}`} className="flex items-center gap-2 text-[11px] font-mono" data-testid={`top-route-${i}`}>
                    <span className="text-gray-400 w-8">{t.method}</span>
                    <span className="flex-1 truncate text-[#1E1B4B]">{t.pattern}</span>
                    <span className="text-gray-600">{t.count}</span>
                    {t.error_rate > 0.05 && (
                      <span className="text-[9px] bg-red-100 text-red-700 px-1 rounded">
                        {Math.round(t.error_rate * 100)}% err
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">
                Dormant routes (consider deprecating)
              </p>
              {usage.dormant_routes.length === 0 ? (
                <p className="text-[11px] text-gray-400 italic">None — all routes seeing traffic 🎉</p>
              ) : (
                <div className="space-y-0.5 max-h-[180px] overflow-y-auto">
                  {usage.dormant_routes.slice(0, 10).map((r) => (
                    <p key={r} className="text-[10px] font-mono text-gray-500">{r}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Last brief */}
      {data.last_brief && (
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm" data-testid="last-brief-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-sm text-[#1E1B4B] flex items-center gap-1.5">
              <Mail size={14} className="text-indigo-500" /> Last Morning Brief
            </h3>
            <span className="text-[10px] text-gray-500 flex items-center gap-1">
              <Clock size={10} /> {fmtTime(data.last_brief.generated_at)}
              {data.last_brief.used_llm && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-bold">Gemini</span>}
            </span>
          </div>
          <pre className="text-xs text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap break-words">
            {data.last_brief.summary}
          </pre>
        </div>
      )}

      {/* Synthetic journey runs (v2.5) */}
      {synthetic && (
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm" data-testid="synthetic-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm text-[#1E1B4B] flex items-center gap-1.5">
              <Zap size={14} className="text-indigo-500" /> Synthetic Journeys (v2.5)
            </h3>
            <span className="text-[10px] text-gray-500">Runs every 20 min · {synthetic.journeys?.length || 0} journeys</span>
          </div>
          {synthetic.runs && synthetic.runs.length > 0 ? (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {synthetic.runs.map(r => (
                <div key={r.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${r.status === 'pass' ? 'hover:bg-emerald-50' : 'bg-red-50/50 hover:bg-red-50'}`} data-testid={`synth-run-${r.id}`}>
                  <span className={`w-2 h-2 rounded-full ${r.status === 'pass' ? 'bg-emerald-500' : 'bg-red-500'}`}/>
                  <span className="font-semibold text-slate-800 w-36 truncate">{r.journey_label}</span>
                  <span className="text-[10px] text-slate-500 flex-1 truncate">
                    {r.steps?.map(s => `${s.ok ? '✓' : '✗'} ${s.step}`).join(' · ') || r.error}
                  </span>
                  <span className="text-[10px] text-slate-400 w-14 text-right">{r.total_ms}ms</span>
                  <span className="text-[10px] text-slate-400 w-24 text-right">{fmtTime(r.ran_at)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500 text-center py-4">No synthetic runs yet. Click "Run synthetic" above.</p>
          )}
        </div>
      )}

      <p className="text-[10px] text-center text-gray-400">
        Probes every 5 min · log digest hourly · daily brief 07:00 IST.
        WhatsApp: +91 9664870766 · Email: sumit@ / vandana@ / business@doozygo.com · Ops chat: @ops.
      </p>
    </div>
  );
};

export default DoozyGuardTab;
